/**
 * Frontend Formula Rule — Evaluate formula expressions in conditional logic.
 *
 * Delegates to GF's native GFCalc.runCalc() so that the entire calculation
 * pipeline runs — gform_calculation_formula filter (GPAC hooks here),
 * replaceFieldTags(), eval(), gform_calculation_result filter — without
 * duplicating any of it.
 *
 * A temporary hidden DOM element is created so runCalc() has somewhere to
 * write its result. The value is read back and the element is removed.
 *
 * Used by advanced-logic.ts when processing formula rules inside
 * the sentinel evaluation.
 */

/** Unique suffix for the temporary DOM element used by runCalc(). */
const TEMP_FIELD_SUFFIX = 'gpacl_formula_eval';

/**
 * Ensure a GFCalc instance exists for the given form.
 *
 * If the form has no native calculation fields, GF never initializes
 * gfcalc[formId]. GPAC's gform_calculation_formula hook reads
 * gfcalc[formId] unconditionally — a minimal stub prevents the crash.
 */
function ensureGFCalcExists(formId: number): void {
	if (window.gf_global && typeof GFCalc === 'function') {
		if (!window.gf_global.gfcalc) {
			window.gf_global.gfcalc = {};
		}
		if (typeof window.gf_global.gfcalc[formId] === 'undefined') {
			window.gf_global.gfcalc[formId] = createSilentGFCalc(formId);
		}
	}
}

/**
 * Create a GFCalc instance without letting it claim the form's shared
 * `gform_post_conditional_logic.gfCalc_{formId}` binding.
 *
 * GFCalc's constructor calls init(), which does
 * `jQuery(document).off(ns).on(ns, () => runCalcs(formId, formulaFields))`.
 * Because the namespace is per-form, the last GFCalc constructed for a form
 * owns that binding. Our stub carries an empty formula list, so if it binds
 * last the form's real calculations stop running on conditional-logic
 * events — which is how GPNF's `:count` modifier updates (GPNF recalculates
 * by triggering `gform_post_conditional_logic`).
 *
 * This is reachable even when GF does have calculation fields: GF assigns
 * with `gf_global.gfcalc[id] = new GFCalc(...)`, and the constructor runs
 * before the assignment lands. Its initial runCalc() writes a value and
 * triggers `change`, which runs conditional logic — and therefore this
 * code — while `gf_global.gfcalc[id]` is still undefined.
 *
 * Suppressing the two namespaced jQuery calls for the duration of the
 * constructor keeps the stub inert without duplicating any of GFCalc.
 *
 * @param formId The form ID.
 * @return The GFCalc instance.
 */
function createSilentGFCalc(formId: number) {
	const $ = window.jQuery;
	const namespace = `.gfCalc_${formId}`;

	if (!$?.fn) {
		return new GFCalc!(formId, []);
	}

	const isOurBinding = (events: unknown) =>
		typeof events === 'string' && events.endsWith(namespace);

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const fn = $.fn as any;
	const originalOff = fn.off;
	const originalOn = fn.on;

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	fn.off = function (this: any, events: unknown, ...rest: unknown[]) {
		return isOurBinding(events)
			? this
			: originalOff.apply(this, [events, ...rest]);
	};
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	fn.on = function (this: any, events: unknown, ...rest: unknown[]) {
		return isOurBinding(events)
			? this
			: originalOn.apply(this, [events, ...rest]);
	};

	try {
		return new GFCalc!(formId, []);
	} finally {
		fn.off = originalOff;
		fn.on = originalOn;
	}
}

/**
 * Evaluate a formula expression for a given form.
 *
 * Delegates to GFCalc.runCalc() so the full native pipeline runs:
 *   1. gform_calculation_formula filter (GPAC hooks at priority 15)
 *   2. replaceFieldTags() — resolves merge tags from the DOM
 *   3. eval() — evaluates the arithmetic expression
 *   4. gform_calculation_result filter
 *
 * A temporary hidden input is injected so runCalc() can write its
 * result. The value is read back and the element is removed.
 *
 * @param formula The formula expression (e.g., "{:1:value} + {:2:value}").
 * @param formId  The form ID.
 * @return The evaluated numeric result, or a string for non-numeric results.
 */
export function evaluateFormula(
	formula: string,
	formId: number
): number | string {
	if (!formula) {
		return 0;
	}

	ensureGFCalcExists(formId);

	const calcObj = window.gf_global?.gfcalc?.[formId];
	if (!calcObj) {
		return 0;
	}

	// Create temporary DOM elements that runCalc() expects:
	//   - A wrapper div with id="field_{formId}_{fieldId}"
	//   - An input with id="input_{formId}_{fieldId}"
	const wrapper = document.createElement('div');
	wrapper.id = `field_${formId}_${TEMP_FIELD_SUFFIX}`;
	wrapper.style.display = 'none';

	const input = document.createElement('input');
	input.id = `input_${formId}_${TEMP_FIELD_SUFFIX}`;
	input.type = 'hidden';
	wrapper.appendChild(input);
	document.body.appendChild(wrapper);

	try {
		calcObj.runCalc(
			{
				field_id: TEMP_FIELD_SUFFIX,
				formula,
				rounding: 'norounding',
			},
			formId
		);
	} catch {
		// Swallow — if runCalc fails the input stays empty → returns 0.
	}

	const raw = input.value;
	wrapper.remove();

	if (!raw) {
		return 0;
	}

	// runCalc() may format the result with thousand separators (e.g. "1,300").
	// Use GF's gformToNumber() to parse locale-aware numbers back to a float.
	if (typeof gformToNumber === 'function') {
		const num = gformToNumber(raw);
		return num || 0;
	}

	// Fallback: strip commas and parse.
	const cleaned = raw.replace(/,/g, '');
	const num = parseFloat(cleaned);
	return isNaN(num) ? raw : num;
}

/**
 * Extract all field IDs referenced in a formula string.
 *
 * @param formula The formula expression.
 * @return Array of field ID strings.
 */
export function getFormulaFieldIds(formula: string): string[] {
	const ids: string[] = [];

	// Match merge tags: {:1:value}, {:2.3:price}, {:4}
	const mergeTagRegex = /{[^}]*?:(\d+(?:\.\d+)?)/g;
	let match;
	while ((match = mergeTagRegex.exec(formula)) !== null) {
		ids.push(match[1]);
	}

	// Match shorthand: F1, F2.3
	const shorthandRegex = /F(\d+(?:\.\d+)?)/g;
	while ((match = shorthandRegex.exec(formula)) !== null) {
		ids.push(match[1]);
	}

	return ids.filter((id, index) => ids.indexOf(id) === index);
}
